import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { auth } from '../api/client';
import {
  ArrowLeft, Mail, Lock, Shield, Check, AlertCircle,
  Eye, EyeOff, Send, KeyRound,
} from 'lucide-react';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';

type Step = 'email' | 'sending' | 'otp' | 'verifying' | 'done';

export default function ForgotPasswordPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const inp = `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep('sending');
    setError('');
    setMsg('');
    try {
      const res = await auth.forgotPassword(email);
      setMsg(res.detail);
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
      setStep('email');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setStep('verifying');
    setError('');
    try {
      await auth.resetPassword(email, otp, newPassword);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
      setStep('otp');
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO {...pageSeoMeta.forgotPassword} />
      <div className={`w-full max-w-md rounded-2xl shadow-xl p-8 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}>

        <Link to="/login" className={`inline-flex items-center gap-1 text-xs mb-6 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
          <ArrowLeft size={14} /> Back to Login
        </Link>

        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <KeyRound className="text-white" size={24} />
          </div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {step === 'done' ? 'Password Reset!' : 'Forgot Password'}
          </h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {step === 'done'
              ? 'Your password has been reset successfully'
              : step === 'otp' || step === 'verifying'
                ? `Enter the OTP sent to ${email}`
                : 'Enter your email to receive a reset OTP'}
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        )}
        {msg && step !== 'done' && (
          <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-500 p-3 rounded-lg mb-4 text-sm">
            <Check size={16} /> {msg}
          </div>
        )}

        {(step === 'otp' || step === 'verifying') && (
          <div className={`flex items-start gap-2 p-3 rounded-lg mb-4 text-sm ${isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>Can't find the email? Check your <strong>Spam</strong> or <strong>Junk</strong> folder. It may take a minute to arrive.</span>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-emerald-600" />
            </div>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              You can now sign in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}

        {/* Step: Enter Email */}
        {(step === 'email' || step === 'sending') && (
          <form onSubmit={handleSendOTP} className="space-y-4">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email Address</label>
              <div className="relative">
                <Mail size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`${inp} pl-10`}
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={step === 'sending'}
              className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              {step === 'sending' ? (
                <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Sending OTP...</>
              ) : (
                <><Send size={16} /> Send Reset OTP</>
              )}
            </button>
          </form>
        )}

        {/* Step: Enter OTP + New Password */}
        {(step === 'otp' || step === 'verifying') && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Enter OTP</label>
              <div className="relative">
                <Shield size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit OTP"
                  maxLength={6}
                  className={`${inp} pl-10 text-center tracking-[0.3em] font-mono text-lg`}
                  required
                />
              </div>
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>New Password</label>
              <div className="relative">
                <Lock size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type={showNewPass ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className={`${inp} pl-10 pr-10`}
                  required
                  minLength={6}
                />
                <button type="button" onClick={() => setShowNewPass(!showNewPass)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {showNewPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Confirm Password</label>
              <div className="relative">
                <Lock size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type={showConfirmPass ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className={`${inp} pl-10 pr-10`}
                  required
                  minLength={6}
                />
                <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {showConfirmPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={step === 'verifying' || otp.length !== 6}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              {step === 'verifying' ? 'Resetting...' : 'Reset Password'}
            </button>

            <button
              type="button"
              onClick={() => { setStep('email'); setOtp(''); setError(''); setMsg(''); }}
              className={`w-full text-center text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
